import { LogOut, BarChart, ChevronDown, LayoutDashboard, SendHorizontal, LifeBuoy, KeyRound } from 'lucide-react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useRevenueAccess } from '../hooks/useRevenueAccess';
import { useState, useRef, useEffect } from 'react';
import { HelpTicketsPanel } from './HelpTicketsPanel';

export default function Header() {
  const { user, logout } = useAuth();
  const { canViewRevenue, isAdmin } = useRevenueAccess();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const handleLogout = async () => {
    try {
      await logout();
    } finally {
      navigate('/login');
    }
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const isReportActive = location.pathname.startsWith('/report');

  const navLinkClass = (isActive: boolean) => `nav-link${isActive ? ' is-active' : ''}`;

  const reportSubLinkClass = (path: string) =>
    [
      'block rounded-lg px-3 py-2.5 text-sm font-medium transition-colors duration-150',
      location.pathname === path
        ? 'bg-action-surface text-action'
        : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900',
    ].join(' ');

  const userInitial = user?.email?.trim()?.[0]?.toUpperCase() ?? '?';

  return (
    <nav
      className="sticky top-0 z-40 border-b border-slate-200/90 bg-white/90 shadow-[0_1px_0_0_rgb(15_23_42/0.04)] backdrop-blur-md backdrop-saturate-150"
      aria-label="Main"
    >
      <div className="mx-auto max-w-[90rem] px-4 sm:px-6 lg:px-8">
        <div className="flex h-14 items-center justify-between gap-4 sm:h-[3.5rem]">
          <div className="flex flex-shrink-0 items-center">
            <Link
              to="/dashboard"
              className="rounded-lg outline-none ring-offset-2 transition-opacity hover:opacity-90 focus-visible:ring-2 focus-visible:ring-action/40"
            >
              <img src="/images/stream2_logo.png" alt="Stream2" className="h-7 w-auto sm:h-8" />
            </Link>
          </div>

          <div className="flex flex-1 justify-center px-4 sm:px-6">
            <div className="hidden items-center gap-0.5 sm:flex">
              <Link
                to="/dashboard"
                className={navLinkClass(location.pathname === '/dashboard')}
              >
                <LayoutDashboard className="h-4 w-4 shrink-0 opacity-90" aria-hidden />
                Dashboard
              </Link>

              <div className="relative group">
                <button type="button" className={navLinkClass(isReportActive)} aria-haspopup="true">
                  <BarChart className="h-4 w-4 shrink-0 opacity-90" aria-hidden />
                  Reports
                  <ChevronDown
                    className="h-3.5 w-3.5 shrink-0 opacity-70 transition-transform duration-200 group-hover:rotate-180"
                    aria-hidden
                  />
                </button>
                <div className="invisible absolute left-0 top-full z-50 min-w-[18rem] pt-1.5 opacity-0 transition-[opacity,visibility] duration-150 ease-out group-hover:visible group-hover:opacity-100">
                  <div className="max-h-[min(70vh,26rem)] overflow-y-auto rounded-xl border border-slate-200/90 bg-white py-1 shadow-lg ring-1 ring-slate-900/[0.04]">
                    <div className="px-1 py-0.5">
                      <Link
                        to="/report/estimated-revenue"
                        className={reportSubLinkClass('/report/estimated-revenue')}
                      >
                        Estimated Revenue per Manager
                      </Link>
                      <Link to="/report/group-series" className={reportSubLinkClass('/report/group-series')}>
                        Group Series Report
                      </Link>
                      <Link
                        to="/report/materialization-per-client"
                        className={reportSubLinkClass('/report/materialization-per-client')}
                      >
                        Materialization per Client / Manager
                      </Link>
                      <Link
                        to="/report/materialization-per-client-hotel"
                        className={reportSubLinkClass('/report/materialization-per-client-hotel')}
                      >
                        Materialization per Client / Hotel
                      </Link>
                      <Link
                        to="/report/client-hotel-materialization"
                        className={reportSubLinkClass('/report/client-hotel-materialization')}
                      >
                        Materialization per Client / City
                      </Link>
                      <Link
                        to="/report/destination-hotel-materialization"
                        className={reportSubLinkClass('/report/destination-hotel-materialization')}
                      >
                        Materialization per Country / Hotel
                      </Link>
                      <Link
                        to="/report/materialization-per-segment"
                        className={reportSubLinkClass('/report/materialization-per-segment')}
                      >
                        Materialization per Segment
                      </Link>
                      <Link
                        to="/report/materialization-per-market-client"
                        className={reportSubLinkClass('/report/materialization-per-market-client')}
                      >
                        Materialization per Market / Client
                      </Link>
                      <Link
                        to="/report/leadership-sales-update"
                        className={reportSubLinkClass('/report/leadership-sales-update')}
                      >
                        Leadership Team Sales update
                      </Link>
                      <Link
                        to="/report/hotels-performance"
                        className={reportSubLinkClass('/report/hotels-performance')}
                      >
                        Hotels Performance
                      </Link>
                      <Link
                        to="/report/average-rates-per-city"
                        className={reportSubLinkClass('/report/average-rates-per-city')}
                      >
                        Average Rates per City
                      </Link>
                      <Link to="/report/lost-business" className={reportSubLinkClass('/report/lost-business')}>
                        Lost Business Report
                      </Link>
                      <Link
                        to="/report/created-confirmed-revenue"
                        className={reportSubLinkClass('/report/created-confirmed-revenue')}
                      >
                        Created / Confirmed Revenue
                      </Link>
                      <Link to="/report/groups-per-month" className={reportSubLinkClass('/report/groups-per-month')}>
                        Groups per Month
                      </Link>
                      <Link
                        to="/report/meeting-preparation"
                        className={reportSubLinkClass('/report/meeting-preparation')}
                      >
                        Meeting Preparation
                      </Link>
                    </div>
                  </div>
                </div>
              </div>

              {canViewRevenue && (
                <Link
                  to="/revenue-collection"
                  className={navLinkClass(
                    location.pathname.startsWith('/revenue-collection') &&
                      !location.pathname.includes('/form/'),
                  )}
                >
                  <SendHorizontal className="h-4 w-4 shrink-0 opacity-90" aria-hidden />
                  Revenue Collection
                </Link>
              )}

              {isAdmin && (
                <Link
                  to="/admin/mcp-access"
                  className={navLinkClass(location.pathname === '/admin/mcp-access')}
                >
                  <KeyRound className="h-4 w-4 shrink-0 opacity-90" aria-hidden />
                  MCP access
                </Link>
              )}
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              onClick={() => setIsHelpOpen(true)}
              aria-label="Help and support"
              aria-expanded={isHelpOpen}
              aria-haspopup="dialog"
              aria-controls="help-tickets-drawer"
              title="Help and support"
              className={[
                'inline-flex h-10 w-10 items-center justify-center rounded-lg text-slate-600 transition-colors duration-150',
                isHelpOpen
                  ? 'bg-action-surface text-action shadow-[inset_0_0_0_1px_rgb(24_141_130/0.12)]'
                  : 'hover:bg-slate-50 hover:text-slate-900',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-action/35 focus-visible:ring-offset-2',
              ].join(' ')}
            >
              <LifeBuoy className="h-5 w-5" aria-hidden />
            </button>
            <HelpTicketsPanel open={isHelpOpen} onClose={() => setIsHelpOpen(false)} />

            <div className="relative" ref={menuRef}>
              <button
                type="button"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                aria-expanded={isMenuOpen}
                aria-haspopup="true"
                className="inline-flex max-w-[14rem] items-center gap-2 rounded-full border border-slate-200/90 bg-white py-1 pl-1 pr-2.5 text-left text-sm shadow-sm transition-[box-shadow,background-color,border-color] hover:border-slate-300/90 hover:bg-slate-50/80 hover:shadow md:max-w-[18rem] focus:outline-none focus-visible:ring-2 focus-visible:ring-action/35 focus-visible:ring-offset-2"
              >
                <span
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-action-surface text-xs font-bold text-action"
                  aria-hidden
                >
                  {userInitial}
                </span>
                <span className="min-w-0 flex-1 truncate font-medium text-slate-700">{user?.email}</span>
                <ChevronDown
                  className={`h-4 w-4 shrink-0 text-slate-500 transition-transform duration-200 ${isMenuOpen ? 'rotate-180' : ''}`}
                  aria-hidden
                />
              </button>

              {isMenuOpen && (
                <div className="absolute right-0 z-50 mt-2 w-56 overflow-hidden rounded-xl border border-slate-200/90 bg-white py-1 shadow-lg shadow-slate-900/10 ring-1 ring-slate-900/[0.04]">
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="mx-1 flex w-[calc(100%-0.5rem)] items-center gap-2 rounded-lg px-3 py-2.5 text-left text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 focus:outline-none focus-visible:bg-slate-50 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-action/30"
                  >
                    <LogOut className="h-4 w-4 shrink-0 text-slate-500" aria-hidden />
                    Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
}
